import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  CreditCard, Package, Calendar, DollarSign,
  CheckCircle, Clock, XCircle, ArrowLeft, ShoppingBag
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_MAP = {
  paid: { label: 'Paid', icon: CheckCircle, color: 'text-green-600 bg-green-50 border-green-200' },
  initiated: { label: 'Pending', icon: Clock, color: 'text-yellow-600 bg-yellow-50 border-yellow-200' },
  failed: { label: 'Failed', icon: XCircle, color: 'text-red-600 bg-red-50 border-red-200' },
};

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function formatTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

export default function PurchaseHistory() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('purchases');

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const { data: result } = await axios.get(`${API_URL}/api/purchases`, { withCredentials: true });
      setData(result);
    } catch (err) {
      setData({ purchases: [], transactions: [] });
    } finally {
      setLoading(false);
    }
  };

  const totalSpent = data?.purchases?.reduce((sum, p) => sum + (p.amount || 0), 0) || 0;
  const purchaseCount = data?.purchases?.length || 0;

  if (loading) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB]" data-testid="purchase-history-page">
      {/* Header */}
      <div className="bg-white border-b border-[#E5E7EB] py-6 md:py-8 px-4 md:px-6">
        <div className="max-w-5xl mx-auto">
          <Link to="/dashboard" className="inline-flex items-center gap-1 text-sm text-[#4B5563] hover:text-[#002FA7] mb-4 transition-colors" data-testid="back-to-dashboard">
            <ArrowLeft className="w-4 h-4" /> Back to Dashboard
          </Link>
          <h1 className="text-xl md:text-3xl font-bold text-[#0A0A0A]" data-testid="purchase-history-title">
            Purchase History
          </h1>
          <p className="text-[#4B5563] mt-1">
            View your past purchases and payment transactions.
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 md:px-6 py-6 md:py-8 space-y-6">
        {/* Stats Row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white border border-[#E5E7EB] rounded-sm p-5" data-testid="stat-total-spent">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#002FA7]/10 rounded-sm flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-[#002FA7]" />
              </div>
              <div>
                <p className="text-sm text-[#4B5563]">Total Spent</p>
                <p className="text-2xl font-black text-[#0A0A0A]">${totalSpent.toFixed(2)}</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-[#E5E7EB] rounded-sm p-5" data-testid="stat-purchases">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-sm flex items-center justify-center">
                <ShoppingBag className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-[#4B5563]">Purchases</p>
                <p className="text-2xl font-black text-[#0A0A0A]">{purchaseCount}</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-[#E5E7EB] rounded-sm p-5" data-testid="stat-member-since">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-sm flex items-center justify-center">
                <Calendar className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-[#4B5563]">Member Since</p>
                <p className="text-2xl font-black text-[#0A0A0A]">{formatDate(user?.created_at)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* View Toggle */}
        <div className="flex gap-2">
          <button
            onClick={() => setActiveView('purchases')}
            className={`px-4 py-2 text-sm font-medium rounded-sm transition-colors flex items-center gap-2 ${activeView === 'purchases' ? 'bg-[#002FA7] text-white' : 'bg-white border border-[#E5E7EB] text-[#4B5563] hover:border-[#002FA7]'}`}
            data-testid="view-purchases-btn"
          >
            <Package className="w-4 h-4" /> Purchases
          </button>
          <button
            onClick={() => setActiveView('transactions')}
            className={`px-4 py-2 text-sm font-medium rounded-sm transition-colors flex items-center gap-2 ${activeView === 'transactions' ? 'bg-[#002FA7] text-white' : 'bg-white border border-[#E5E7EB] text-[#4B5563] hover:border-[#002FA7]'}`}
            data-testid="view-transactions-btn"
          >
            <CreditCard className="w-4 h-4" /> All Transactions
          </button>
        </div>

        {/* Purchases View */}
        {activeView === 'purchases' && (
          <div className="space-y-3" data-testid="purchases-list">
            {data?.purchases?.length === 0 ? (
              <div className="bg-white border border-[#E5E7EB] rounded-sm p-12 text-center">
                <ShoppingBag className="w-12 h-12 text-[#9CA3AF] mx-auto mb-4" />
                <p className="text-[#4B5563] mb-4">No purchases yet</p>
                <Link to="/pricing">
                  <Button className="bg-[#002FA7] hover:bg-[#001B63] rounded-sm" data-testid="browse-packages-btn">
                    Browse Packages
                  </Button>
                </Link>
              </div>
            ) : (
              data?.purchases?.map((purchase, idx) => (
                <div
                  key={idx}
                  className="bg-white border border-[#E5E7EB] rounded-sm p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4"
                  data-testid={`purchase-item-${idx}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-[#002FA7]/10 rounded-sm flex items-center justify-center flex-shrink-0">
                      <Package className="w-5 h-5 text-[#002FA7]" />
                    </div>
                    <div>
                      <p className="font-medium text-[#0A0A0A]">{purchase.package_name}</p>
                      <p className="text-xs text-[#9CA3AF]">
                        {formatDate(purchase.purchased_at)} at {formatTime(purchase.purchased_at)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 sm:gap-6">
                    <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-sm border text-green-600 bg-green-50 border-green-200">
                      <CheckCircle className="w-3 h-3" /> Paid
                    </span>
                    <span className="text-lg font-bold text-[#0A0A0A]" data-testid={`purchase-amount-${idx}`}>
                      ${purchase.amount?.toFixed(2)}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Transactions View */}
        {activeView === 'transactions' && (
          <div className="space-y-3" data-testid="transactions-list">
            {data?.transactions?.length === 0 ? (
              <div className="bg-white border border-[#E5E7EB] rounded-sm p-12 text-center">
                <CreditCard className="w-12 h-12 text-[#9CA3AF] mx-auto mb-4" />
                <p className="text-[#4B5563]">No transactions found</p>
              </div>
            ) : (
              data?.transactions?.map((tx, idx) => {
                const status = STATUS_MAP[tx.payment_status] || STATUS_MAP.initiated;
                const StatusIcon = status.icon;
                return (
                  <div
                    key={idx}
                    className="bg-white border border-[#E5E7EB] rounded-sm p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4"
                    data-testid={`transaction-item-${idx}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-[#F3F4F6] rounded-sm flex items-center justify-center flex-shrink-0">
                        <CreditCard className="w-5 h-5 text-[#4B5563]" />
                      </div>
                      <div>
                        <p className="font-medium text-[#0A0A0A] capitalize">
                          {tx.type === 'package' ? (tx.package_id?.replace(/_/g, ' ') || 'Package') : 'Monthly Subscription'}
                        </p>
                        <p className="text-xs text-[#9CA3AF]">
                          {formatDate(tx.created_at)} at {formatTime(tx.created_at)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 sm:gap-6">
                      <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-sm border ${status.color}`}>
                        <StatusIcon className="w-3 h-3" /> {status.label}
                      </span>
                      <span className="text-lg font-bold text-[#0A0A0A]" data-testid={`transaction-amount-${idx}`}>
                        ${tx.amount?.toFixed(2)}
                      </span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
}
